import type { DevOverrides } from './devOverrides';
import type { Locale, MessageKey } from './messages';
import { messages } from './messages';

function getMessageFromTree(locale: Locale, key: MessageKey) {
  return key.split('.').reduce<unknown>((acc, part) => {
    if (typeof acc === 'object' && acc && part in acc) {
      return (acc as Record<string, unknown>)[part];
    }

    return undefined;
  }, messages[locale]);
}

export function getBaseMessage(
  locale: Locale,
  key: MessageKey
): string | undefined {
  const message = getMessageFromTree(locale, key);
  return typeof message === 'string' ? message : undefined;
}

/** Dev overrides must be non-empty strings and must not echo the message key. */
export function isUsableDevOverride(
  value: unknown,
  key: MessageKey
): value is string {
  return (
    typeof value === 'string' && value.trim().length > 0 && value !== key
  );
}

export function resolveMessage(
  locale: Locale,
  key: MessageKey,
  overrides?: DevOverrides
): string | undefined {
  const base = getBaseMessage(locale, key);
  const override = overrides?.[locale]?.[key];

  if (isUsableDevOverride(override, key)) {
    return override;
  }

  return base;
}

export function interpolateMessage(
  template: string,
  params?: Record<string, string | number>
) {
  if (!params) {
    return template;
  }

  return template.replace(/\{(\w+)\}/g, (_, token: string) =>
    String(params[token] ?? `{${token}}`)
  );
}
